import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import {
  getCollection,
  find as apiFindRecord,
  createOrUpdate as apiCreateOrUpdate,
  deleteRecord as apiDeleteRecord,
} from 'api/market-view-api';
import type { GetCollectionOptions } from 'api/market-view-api';
import type {
  FilterValue,
  SortDirection,
} from 'helpers/api/build-query-params';

import { transformRecordFromServer } from 'helpers/server-data-transformers';

import { currentUserKeys, useCurrentUser } from 'hooks/api/use-current-user';
import type { UserPartialUpdateRequest } from 'api/generated/schemas/userPartialUpdateRequest';
import { partialUpdateUser } from 'api/generated/users/users';

import Notification from 'components/partials/notification/notification.helper';

import { findClient } from 'hooks/api/use-clients';

export interface UserRecord {
  id: string | number;
  status?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  authority?: string;
  fdUser?: boolean;
  viewPortalAsUser?: boolean;
  email?: string;
  superAdmin?: boolean;
  client?: {
    id?: string | number;
    name?: string;
    status?: string;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  pageCount: number;
  totalRecords: number;
}

interface CollectionLoadResponse {
  data: UserRecord[];
  pagination: PaginationInfo;
}

export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) =>
    [...usersKeys.lists(), params] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...usersKeys.details(), id] as const,
};

const transformUser = (data: unknown): UserRecord =>
  transformRecordFromServer('user', data) as UserRecord;

// ─── List query (paginated, used by users-admin-page) ─────────────────────

export interface UseUsersListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
  searchTerms?: string;
  enabled?: boolean;
}

export interface UseUsersListResult {
  data: UserRecord[];
  pagination: PaginationInfo | null;
  loading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

const EMPTY_USERS: UserRecord[] = [];

export const useUsersList = (
  params: UseUsersListParams = {}
): UseUsersListResult => {
  const {
    pageSize = 10,
    pageIndex = 0,
    sortBy = [],
    filters = {},
    searchTerms = '',
    enabled = true,
  } = params;

  const queryKey = usersKeys.list({
    pageSize,
    pageIndex,
    sortBy,
    filters,
    searchTerms,
  });

  const query: UseQueryResult<CollectionLoadResponse, Error> = useQuery({
    queryKey,
    queryFn: async () => {
      const options: GetCollectionOptions = {
        pageSize,
        pageIndex,
        sortBy,
        filters,
        searchTerms,
        forceSearch: false,
      };

      const response = await getCollection('users', options);
      const records = response.data as unknown[];

      const transformed = Array.isArray(records)
        ? records.map(transformUser)
        : [];

      return { data: transformed, pagination: response.pagination };
    },
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data?.data ?? EMPTY_USERS,
    pagination: query.data?.pagination ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

// ─── Single user query (replaces users.find) ─────────────────────────────────

export interface UseUserResult {
  data: UserRecord | null;
  loading: boolean;
  error: unknown;
}

export const useUser = (
  userId: string | number | null | undefined
): UseUserResult => {
  const query: UseQueryResult<UserRecord, Error> = useQuery({
    queryKey: usersKeys.detail(userId ?? ''),
    queryFn: async () => {
      const response = await apiFindRecord('users', userId!);

      return transformUser(response.data);
    },
    enabled: !!userId,
  });

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
};

// ─── Imperative find (for impersonation-banner, user-page) ───────────────────

export const findUser = async (
  id: string | number
): Promise<{ data: UserRecord }> => {
  const response = await apiFindRecord('users', id);

  return { data: transformUser(response.data) };
};

// ─── findAndAddClientData (replaces useUsers().findAndAddClientData) ──────────

export const findAndAddClientData = async (
  userId: string | number
): Promise<{ data: UserRecord }> => {
  const userResponse = await apiFindRecord('users', userId);
  const user = transformUser(userResponse.data);

  if (user.client?.id) {
    try {
      const clientResponse = await findClient(user.client.id);

      return {
        data: {
          ...user,
          client: { ...clientResponse.data },
        },
      };
    } catch {
      return { data: user };
    }
  }

  return { data: user };
};

// ─── Mutations ──────────────────────────────────────────────────────────────

export const useCreateOrUpdateUser = (): UseMutationResult<
  { data: UserRecord },
  Error,
  Record<string, unknown>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiCreateOrUpdate('users', data);

      return { data: transformUser(response.data) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      queryClient.invalidateQueries({ queryKey: currentUserKeys.all });
    },
  });
};

export const usePartialUpdateUserMutation = (): UseMutationResult<
  { data: UserRecord },
  Error,
  { id: string | number } & UserPartialUpdateRequest
> => {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  return useMutation({
    mutationFn: async ({ id, ...changes }) => {
      if (!currentUser?.oktaUserId) {
        throw new Error('Current user Okta id is required to update a user');
      }

      const response = await partialUpdateUser(
        String(currentUser.oktaUserId),
        Number(id),
        changes
      );

      return { data: transformUser(response) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      queryClient.invalidateQueries({ queryKey: currentUserKeys.all });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string | number; name?: string }>({
    mutationFn: async ({ id }) => {
      await apiDeleteRecord('users', id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
};

export const useLockUser = (): UseMutationResult<
  { data: UserRecord },
  Error,
  UserRecord
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user) => {
      const isLocked = user.status === 'LOCKED';
      const updated = {
        ...user,
        status: isLocked ? 'ACTIVE' : 'LOCKED',
      };
      const response = await apiCreateOrUpdate('users', updated);

      return { data: transformUser(response.data) };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      queryClient.invalidateQueries({ queryKey: currentUserKeys.all });

      const changeMsg = result.data.status === 'LOCKED' ? 'locked' : 'unlocked';
      const changeColor = result.data.status === 'LOCKED' ? 'info' : 'success';

      Notification.flash(
        `User ${changeMsg}`,
        changeColor,
        `You've successfully ${changeMsg} this user`
      );
    },
  });
};
